import { useEffect, useState } from "react";
import {
  Paper,
  Typography,
  Box,
  Link,
  Fade,
  Button,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@workos-inc/authkit-react";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import LocalPoliceOutlinedIcon from "@mui/icons-material/LocalPoliceOutlined";
import logoDark from "../../assets/Lockup Horizontal Dark.svg";
import logoLight from "../../assets/Lockup Horizontal Light.svg";
import { hardRedirectToLogin } from "../../utils/authRecovery";

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.05); }
`;

const ICONS = [
  LocalPoliceOutlinedIcon,
  SecurityOutlinedIcon,
  AssignmentIndOutlinedIcon,
  VerifiedUserOutlinedIcon,
];

const LeftPanel = styled(Box)(({ theme }) => ({
  flex: "0 0 60%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(4),
  position: "relative",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: { display: "none" },
}));

const RightPanel = styled(Box)(({ theme }) => ({
  flex: "1 1 40%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.dark} 100%)`,
  padding: theme.spacing(4),
  position: "relative",
  [theme.breakpoints.down("md")]: { flex: "1 1 100%", width: "100%" },
}));

const DecorativeLine = styled(Box)({
  position: "absolute",
  bottom: 0,
  right: 0,
  width: "300px",
  height: "300px",
  border: "2px solid rgba(255, 255, 255, 0.1)",
  borderRadius: "50%",
  transform: "translate(30%, 30%)",
});

const LoginCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  borderRadius: "24px",
  background: theme.palette.background.paper,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 20px 60px rgba(0, 0, 0, 0.5)"
      : "0 20px 60px rgba(0, 0, 0, 0.15)",
  position: "relative",
  overflow: "hidden",
  width: "100%",
  maxWidth: "480px",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(4),
    margin: theme.spacing(2),
    borderRadius: "16px",
    maxWidth: "400px",
  },
}));

const IconBox = styled(Box)(({ theme }) => ({
  width: 140,
  height: 140,
  borderRadius: "24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.palette.background.paper,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 8px 30px rgba(0, 0, 0, 0.3)"
      : "0 8px 30px rgba(0, 0, 0, 0.08)",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 12px 40px rgba(0, 0, 0, 0.5)"
        : "0 12px 40px rgba(0, 0, 0, 0.15)",
  },
}));

const LoginPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, isLoading, signIn } = useAuth();
  const [isSigningIn, setIsSigningIn] = useState(false);

  const handleSignIn = async () => {
    if (isSigningIn) return;
    setIsSigningIn(true);
    try {
      await signIn();
    } catch {
      setIsSigningIn(false);
      hardRedirectToLogin();
    }
  };

  // Reset signing-in state when the page becomes visible again (e.g. browser back navigation)
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === "visible") setIsSigningIn(false);
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, []);

  useEffect(() => {
    if (!isLoading && user) navigate("/", { replace: true });
  }, [isLoading, user, navigate]);

  const isSubmitDisabled = isLoading || isSigningIn;

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        width: "100%",
        overflow: "hidden",
        backgroundColor: "background.default",
      }}
    >
      <Fade in={isSigningIn} timeout={300} unmountOnExit>
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
            backdropFilter: "blur(8px)",
            backgroundColor: "background.default",
          }}
        >
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                border: (t) => `3px solid ${t.palette.divider}`,
                animation: `${pulse} 2s ease-in-out infinite`,
              }}
            />
            <CircularProgress
              size={48}
              sx={{ color: "primary.main", position: "absolute" }}
            />
          </Box>
          <Typography
            variant="h6"
            sx={{ color: "text.secondary", fontWeight: 500 }}
          >
            Redirecting to sign in...
          </Typography>
        </Box>
      </Fade>

      <LeftPanel>
        <Fade in timeout={800}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              maxWidth: 600,
              p: 2.5,
            }}
          >
            <Box
              component="img"
              src={theme.palette.mode === "dark" ? logoLight : logoDark}
              alt="KARDA Logo"
              sx={{ width: 400, mb: 4 }}
            />

            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                mb: 4,
                letterSpacing: "-0.02em",
              }}
            >
              ReportGen
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 4,
                mb: 5,
              }}
            >
              {ICONS.map((Icon, i) => (
                <IconBox key={i}>
                  <Icon sx={{ fontSize: 64, color: "primary.main" }} />
                </IconBox>
              ))}
            </Box>

            <Typography
              variant="h5"
              sx={{ color: "text.secondary", maxWidth: 600, lineHeight: 1.6 }}
            >
              Streamlined, secure, and reliable
              <br />
              police report generation.
            </Typography>
          </Box>
        </Fade>
      </LeftPanel>

      <RightPanel>
        <DecorativeLine />
        <DecorativeLine sx={{ width: 400, height: 400 }} />

        <Fade in timeout={600}>
          <LoginCard elevation={0}>
            <Typography
              component="h1"
              variant="h4"
              sx={{ fontWeight: 700, color: "text.primary", mb: 3 }}
            >
              Welcome Back
            </Typography>

            <Button
              variant="contained"
              fullWidth
              onClick={handleSignIn}
              disabled={isSubmitDisabled}
              sx={{
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
              }}
            >
              {isSigningIn ? "Redirecting..." : "Sign In"}
            </Button>
          </LoginCard>
        </Fade>

        <Typography
          variant="body2"
          align="center"
          sx={{
            mt: 4,
            color: "rgba(255, 255, 255, 0.9)",
            letterSpacing: "0.02em",
          }}
        >
          {"Copyright \u00A9 "}
          <Link
            color="inherit"
            href="#"
            sx={{
              fontWeight: 500,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            KARDA
          </Link>{" "}
          {new Date().getFullYear()}
        </Typography>
      </RightPanel>
    </Box>
  );
};

export default LoginPage;
